import { createContext, useCallback, useEffect, useState } from 'react'

import { useParams } from 'react-router-dom'

import { MapMarkerProps } from '../../components/MapCard/map-card.types'
import {
  ICompanyProfileDocuments,
  ICompanyResponse,
  ISocialEnvironmentalRisk,
} from '../../sdk/company/company.types'
import { IItemsStatisticsDTO } from '../../sdk/traceability/traceability.types'
import { sdk } from '../../services/sdk'

interface ProviderDataContext {
  loading: boolean
  data: ICompanyResponse | null
  location: MapMarkerProps | null
  documents: ICompanyProfileDocuments[] | null
  companyTraceabilityStatistics: IItemsStatisticsDTO | null
  socialEnvironmentalRisk: ISocialEnvironmentalRisk[] | null
  activeTab: string

  actions: {
    getDocuments: () => Promise<void>
    getCompany: () => Promise<void>
    setActiveTab: (value: string) => void
  }
}

interface ProviderProviderProps {
  children: React.ReactNode
}

export const ProviderContext = createContext({} as ProviderDataContext)

export function ProviderProvider(props: ProviderProviderProps) {
  const params = useParams()
  const [activeTab, setActiveTab] = useState('Documentos')

  const [data, setData] = useState<ICompanyResponse | null>(null)
  const [location, setLocation] = useState<MapMarkerProps | null>(null)

  const [loading, setLoading] = useState(false)
  const [documents, setDocuments] = useState<ICompanyProfileDocuments[] | null>(
    null,
  )
  const [socialEnvironmentalRisk, setSocialEnvironmentalRisk] = useState<
    ISocialEnvironmentalRisk[] | null
  >(null)
  const [companyTraceabilityStatistics, setCompanyTraceabilityStatistics] =
    useState<IItemsStatisticsDTO | null>(null)

  const getCompany = useCallback(async () => {
    try {
      setLoading(true)
      const result = await sdk.company.getCompany(params.providerTaxId ?? '')
      if (result) {
        setData(result)
        setLocation(result.address?.geoCoords?.coords ?? null)
      }
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }, [params])

  const getCompanyTraceabilityStatistics = useCallback(async () => {
    try {
      setLoading(true)
      const result = await sdk.traceability.companyStatistics(
        params.providerTaxId ?? '',
      )
      setCompanyTraceabilityStatistics(result)
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }, [params])

  const getDocuments = async () => {
    try {
      const _documents = await sdk.company.getDocuments(data?.id ?? '')
      setDocuments(_documents)
    } catch (error) {}
  }

  const getSocialEnvironmentalRisk = useCallback(async () => {
    try {
      const _response = await sdk.company.getSocialEnvironmentalRisk(
        params.providerTaxId ?? '',
      )

      setSocialEnvironmentalRisk(_response.data)
    } catch (error) {}
  }, [params])

  useEffect(() => {
    getCompany()
    getCompanyTraceabilityStatistics()
    getSocialEnvironmentalRisk()
  }, [])

  return (
    <ProviderContext.Provider
      value={{
        loading,
        data,
        location,
        documents,
        companyTraceabilityStatistics,
        socialEnvironmentalRisk,
        activeTab,
        actions: {
          getDocuments,
          getCompany,
          setActiveTab,
        },
      }}
    >
      {props.children}
    </ProviderContext.Provider>
  )
}
